import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import CringeShell from './CringeShell';
import { Popup, RotateNotice, TopBar, useSaveScore } from './common';
import { useGameTimer, useIsMobile, useRotateNotice } from './hooks';

const FINDS = 2; // the SVG's two .find groups: pos="0" (Candy) and pos="1" (Chocolate)

function Stage1Game() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const session = useSelector((s) => s.cringe.session);
  const mobile = useIsMobile();
  const rotate = useRotateNotice();
  const timer = useGameTimer(true, '00:00'); // stage 1 always starts the clock from 0
  const save = useSaveScore();
  const hostRef = useRef(null);
  const found = useRef(new Set());
  const popupOpen = useRef(false);
  const [svg, setSvg] = useState('');
  const [points, setPoints] = useState(0);
  const [popup, setPopup] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/cringe/img/stage1/stage1_${mobile ? 'mob' : 'desk'}.svg`)
      .then((r) => r.text())
      .then((text) => alive && setSvg(text))
      .catch((e) => console.error('Stage 1 artwork failed to load', e));
    return () => {
      alive = false;
    };
  }, [mobile]);

  // Wire the hidden objects every time the (desktop or mobile) artwork is put in the page.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svg) return undefined;
    const targets = Array.from(host.querySelectorAll('.find'));
    const onFind = (event) => {
      event.preventDefault();
      if (popupOpen.current) return;
      const el = event.currentTarget;
      const pos = el.getAttribute('pos');
      if (found.current.has(pos)) return;
      popupOpen.current = true;
      found.current.add(pos);
      el.style.display = 'none';
      setPopup(true);
      const score = found.current.size;
      setPoints(score);
      save(1, score, timer.current(), { quiet: true });
    };
    targets.forEach((el) => {
      if (found.current.has(el.getAttribute('pos'))) el.style.display = 'none';
      el.style.cursor = 'pointer';
      el.addEventListener('click', onFind);
      el.addEventListener('touchstart', onFind, { passive: false });
    });
    return () =>
      targets.forEach((el) => {
        el.removeEventListener('click', onFind);
        el.removeEventListener('touchstart', onFind);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svg]);

  const closePopup = () => {
    setPopup(false);
    popupOpen.current = false;
    if (found.current.size < FINDS) return;
    if (session?.sessionId === 'demobypass') {
      Swal.fire({
        title: 'Subscribe to any PLAN to play with your peers.',
        icon: 'info',
        width: '470px',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      setTimeout(() => {
        window.location.href = `${process.env.REACT_APP_BASE_URL}/plans`;
      }, 3000);
    } else {
      navigate({ pathname: '/stage2', search });
    }
  };

  return (
    <>
      {rotate && <RotateNotice />}
      <TopBar time={timer.text} points={points} />
      <div className="cr-stage1-svg" ref={hostRef} dangerouslySetInnerHTML={{ __html: svg }} />
      {popup && (
        <Popup onClose={closePopup}>
          <h2>Wow, looks like you can decode stealth mode!</h2>
          <h3>You’re headed in the right direction.</h3>
        </Popup>
      )}
    </>
  );
}

// stage1.php: spot Cringe's two hidden treats in the scene, 1 point each.
export default function CringeStage1() {
  return (
    <CringeShell bg="stage1/bg.png" bgMob="stage1/bg.png" stage={1} className="cr-stage cr-stage1">
      <Stage1Game />
    </CringeShell>
  );
}
